import * as ex from 'excalibur';

/**
 * Custom grayscale post-processor (we're not using it currently)
 */
class GrayscalePostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      // our texture
      uniform sampler2D u_image;
      // the texCoords passed in from the vertex shader.
      in vec2 v_texcoord;
      out vec4 fragColor;
      void main() {
        vec4 tex = texture(u_image, v_texcoord);
        float avg = 0.2126 * tex.r + 0.7152 * tex.g + 0.0722 * tex.b;
        fragColor = vec4(avg, avg, avg, 1.0);
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }
}
/**
 * Subtle CRT/retro screen post-processor with minimal distortion
 */
class RetroScreenPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;
  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      // our texture
      uniform sampler2D u_image;
      uniform float u_time;
      // the texCoords passed in from the vertex shader.
      in vec2 v_texcoord;
      out vec4 fragColor;

      // Subtle distortion function for minimal CRT curvature
      vec2 barrel(vec2 uv, float amount) {
        vec2 centered = uv * 2.0 - 1.0;
        float z = sqrt(1.0 - centered.x * centered.x * 0.3 - centered.y * centered.y * 0.3);
        vec2 distorted = centered / (z * amount + (1.0 - amount));
        return distorted * 0.5 + 0.5;
      }

      // Emulates a subtle CRT display effect
      vec4 crt_emulate(sampler2D image, vec2 uv, float time) {
        // Scanlines - more subtle and less intense
        float scanlineSize = 240.0;
        float scanlineIntensity = 0.08; // Reduced intensity
        float scanlineY = floor(uv.y * scanlineSize);
        float scanline = step(0.5, mod(scanlineY, 2.0)) * scanlineIntensity;

        // Very subtle RGB separation
        float shift = 0.001; // Reduced RGB shift
        vec4 texR = texture(image, uv + vec2(shift, 0.0));
        vec4 texG = texture(image, uv);
        vec4 texB = texture(image, uv - vec2(shift, 0.0));

        // Subtle vignette
        float vignetteStrength = 0.15;
        float vignetteSize = 0.8;
        float dist = length(uv - 0.5);
        float vignette = 1.0 - vignetteStrength * smoothstep(vignetteSize, 1.0, dist);

        // Minimal flickering effect
        float flicker = 0.985 + 0.015 * sin(time * 4.0);

        // Combine effects
        vec4 color = vec4(texR.r, texG.g, texB.b, 1.0);

        // Apply scanlines more subtly
        if (mod(scanlineY, 2.0) < 0.5) {
            color.rgb *= (1.0 - scanlineIntensity);
        }

        // Apply vignette and flicker
        color.rgb *= vignette * flicker;

        // Very subtle glow for bright areas
        float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));

        // Create subtle bloom
        vec3 bloomColor = vec3(0.0);
        if (luminance > 0.6) { // Only bloom very bright areas
            bloomColor = color.rgb * (luminance - 0.6) * 0.5;

            // Apply minimal color adjustment to the bloom
            bloomColor.r *= 1.05;
            bloomColor.b *= 1.03;
        }

        // Add bloom with modest intensity
        color.rgb += bloomColor * 0.6;

        // Very subtle gamma adjustment
        color.rgb = pow(color.rgb, vec3(0.95));

        return color;
      }

      void main() {
        // Apply extremely subtle barrel distortion
        float distortionAmount = 0.15; // Minimal fisheye effect (0.0 = none, 1.0 = full)
        vec2 distortedCoord = barrel(v_texcoord, 1.0 - distortionAmount);

        // Check if the distorted coordinates are outside the texture
        if (distortedCoord.x < 0.0 || distortedCoord.x > 1.0 ||
            distortedCoord.y < 0.0 || distortedCoord.y > 1.0) {
            // Outside the texture, return black
            fragColor = vec4(0.0, 0.0, 0.0, 1.0);
        } else {
            // Apply CRT effects
            fragColor = crt_emulate(u_image, distortedCoord, u_time);
        }
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  /**
   * Update shader uniforms
   */
  update(elapsed: number): void {
    this._time += elapsed / 1000;
    // Update uniform differently - storing the time for other methods
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Pixel-art enhancing post processor
 */
class PixelArtPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      // our texture
      uniform sampler2D u_image;
      // the texCoords passed in from the vertex shader.
      in vec2 v_texcoord;
      out vec4 fragColor;

      void main() {
        // Original color
        vec4 original = texture(u_image, v_texcoord);

        // Sample neighboring pixels
        float pixelSize = 0.001; // Much smaller pixels for a subtle effect
        vec4 pixelUp = texture(u_image, v_texcoord + vec2(0.0, pixelSize));
        vec4 pixelDown = texture(u_image, v_texcoord - vec2(0.0, pixelSize));
        vec4 pixelLeft = texture(u_image, v_texcoord - vec2(pixelSize, 0.0));
        vec4 pixelRight = texture(u_image, v_texcoord + vec2(pixelSize, 0.0));

        // Edge detection
        vec4 edges = abs(pixelUp - pixelDown) + abs(pixelLeft - pixelRight);
        float edgeFactor = clamp(length(edges.rgb) * 1.5, 0.0, 1.0);

        // Contrast boost
        vec4 contrast = original;
        contrast.rgb = (contrast.rgb - 0.5) * 1.1 + 0.5;

        // Enhance saturation very slightly
        float luminance = dot(original.rgb, vec3(0.299, 0.587, 0.114));
        vec3 satColor = mix(vec3(luminance), original.rgb, 1.1);

        // Combine effects - sharpen edges, boost contrast in midtones
        vec4 pixelated = mix(original, contrast, 0.2);
        pixelated.rgb = mix(pixelated.rgb, satColor, 0.3);

        // Boost edge contrast
        pixelated.rgb = mix(pixelated.rgb, original.rgb, edgeFactor * 0.7);

        fragColor = pixelated;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }
}

/**
 * Time of day - Dawn post-processor
 */
class DawnPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Warm orange/pink tones
        color.r = min(1.0, color.r * 1.2);
        color.g = min(1.0, color.g * 1.0);
        color.b = min(1.0, color.b * 0.9);

        // Add subtle color gradient from bottom (warmer) to top (cooler)
        float gradientFactor = 0.2 * (1.0 - v_texcoord.y);
        color.rgb = mix(color.rgb, vec3(1.0, 0.7, 0.4), gradientFactor);

        // Add subtle sun glow on horizon
        float sunPos = 0.1 + 0.05 * sin(u_time * 0.2); // Slight movement
        float distToSun = distance(v_texcoord, vec2(0.5, sunPos));
        if (distToSun < 0.3) {
          float glow = 0.15 * (1.0 - distToSun/0.3);
          color.rgb += vec3(glow * 0.9, glow * 0.6, glow * 0.3);
        }

        // Add very subtle light rays
        float rayStrength = 0.03 * max(0.0, 1.0 - distToSun * 5.0);
        float ray = rayStrength * (0.5 + 0.5 * sin(v_texcoord.x * 30.0 + u_time));
        color.rgb += vec3(ray, ray * 0.7, ray * 0.3);

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Time of day - Day post-processor
 */
class DayPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Slight vibrance boost for a bright day
        float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
        vec3 saturatedColor = mix(vec3(luminance), color.rgb, 1.1);
        color.rgb = mix(color.rgb, saturatedColor, 0.3);

        // Subtle blue sky enhancement
        if (v_texcoord.y < 0.5) { // Upper part of screen
          color.rgb = mix(color.rgb, vec3(0.5, 0.7, 1.0), 0.05);
        }

        // Occasional subtle lens flare
        float flareTime = mod(u_time * 0.1, 10.0);
        if (flareTime < 1.0) {
          float flareX = 0.6 + 0.3 * sin(u_time * 0.05);
          float flareY = 0.3 + 0.1 * cos(u_time * 0.07);
          float distToFlare = distance(v_texcoord, vec2(flareX, flareY));

          if (distToFlare < 0.1) {
            float flare = 0.1 * (1.0 - distToFlare/0.1) * (1.0 - flareTime);
            color.rgb += vec3(flare);
          }
        }

        // Subtle vignette for natural framing
        float vignette = 1.0 - 0.1 * length(v_texcoord - 0.5);
        color.rgb *= vignette;

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Time of day - Sunset post-processor
 */
class SunsetPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Strong warm tones (orange/red)
        color.r = min(1.0, color.r * 1.3);
        color.g = min(1.0, color.g * 0.9);
        color.b = min(1.0, color.b * 0.8);

        // Gradient from bottom (orange) to top (purple)
        float skyGradient = smoothstep(0.3, 0.7, v_texcoord.y);
        vec3 skyTop = vec3(0.3, 0.2, 0.5); // Purple/blue
        vec3 skyBottom = vec3(0.9, 0.5, 0.2); // Orange

        vec3 skyColor = mix(skyBottom, skyTop, skyGradient);
        color.rgb = mix(color.rgb, skyColor, 0.25);

        // Setting sun on horizon
        float sunX = 0.5 + 0.1 * sin(u_time * 0.1);
        float distToSun = distance(v_texcoord, vec2(sunX, 0.3));
        if (distToSun < 0.15) {
          float glow = 0.2 * (1.0 - distToSun/0.15);
          color.rgb += vec3(glow, glow * 0.5, 0.0);
        }

        // Lens flare streak
        float flare = 0.05 * max(0.0, 1.0 - abs(v_texcoord.y - 0.3) * 10.0)
                  * max(0.0, 1.0 - abs(v_texcoord.x - sunX) * 3.0);
        color.rgb += vec3(flare, flare * 0.7, flare * 0.3);

        // Darken bottom part of screen for contrast
        color.rgb *= mix(1.0, 0.9, (1.0 - v_texcoord.y) * (1.0 - v_texcoord.y));

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Time of day - Night post-processor (enhanced version)
 */
class NightPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      // Function to create a star field
      float stars(vec2 uv, float time) {
        float brightness = 0.0;
        // Create stars with different frequencies
        for (int i = 0; i < 3; i++) {
          float scale = float(i + 1) * 20.0;
          vec2 starPos = floor(uv * scale) + 0.5;
          float starVal = fract(sin(dot(starPos, vec2(12.9898, 78.233)) + time) * 43758.5453);

          // Only show some stars
          if (starVal > 0.97) {
            // Twinkle effect
            float twinkle = 0.5 + 0.5 * sin(time * (starVal * 5.0));
            brightness += twinkle * 0.3 * (starVal - 0.97) * 30.0;
          }
        }
        return brightness;
      }

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Shift colors toward dark blue
        color.r *= 0.7;
        color.g *= 0.8;
        color.b *= 1.0;

        // Darken overall
        color.rgb = mix(color.rgb, vec3(0.05, 0.07, 0.15), 0.5);

        // Add blue tint to shadows
        float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
        if (luminance < 0.3) {
          color.rgb = mix(color.rgb, vec3(0.05, 0.1, 0.25), 0.4);
        }

        // Add moon in sky
        float moonX = 0.75 + 0.05 * sin(u_time * 0.1);
        float moonY = 0.25 + 0.05 * cos(u_time * 0.07);
        float distToMoon = distance(v_texcoord, vec2(moonX, moonY));

        if (distToMoon < 0.07) {
          // Moon disk
          float moonIntensity = 1.0 - distToMoon/0.07;
          vec3 moonColor = vec3(0.9, 0.9, 1.0); // Slightly blue-white

          // Moon craters (subtle texture)
          float craterNoise = fract(sin(dot(v_texcoord * 50.0, vec2(12.9898, 78.233))) * 43758.5453);
          moonColor *= mix(0.95, 1.0, craterNoise);

          color.rgb = mix(color.rgb, moonColor, moonIntensity * 0.9);
        }

        // Add moon glow
        if (distToMoon < 0.2) {
          float glowIntensity = 0.15 * (1.0 - distToMoon/0.2);
          color.rgb += vec3(glowIntensity * 0.7, glowIntensity * 0.8, glowIntensity);
        }

        // Add stars in the sky (upper part of screen)
        if (v_texcoord.y < 0.6) {
          float starIntensity = stars(v_texcoord, u_time);
          color.rgb += vec3(starIntensity);
        }

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Season - Spring post-processor
 */
class SpringPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Enhance greens for fresh growth
        if (color.g > color.r && color.g > color.b) {
          color.g = min(1.0, color.g * 1.15);
        }

        // Add a subtle fresh and bright look
        color.rgb = mix(color.rgb, vec3(0.9, 1.0, 0.9), 0.08);

        // Add subtle pastel tones
        float pastelAmount = 0.05;
        vec3 pastelTint = vec3(0.9, 1.0, 0.8); // Light green-yellow
        color.rgb = mix(color.rgb, pastelTint, pastelAmount);

        // Add subtle petal particles occasionally floating through the scene
        float petals = 0.0;
        for (int i = 0; i < 5; i++) {
          float t = u_time * 0.2 + float(i) * 1.618;
          float scale = 100.0 + float(i) * 50.0;

          vec2 center = vec2(
            mod(t * 0.3 + sin(t * 0.2), 1.0),
            mod(t * 0.2 + cos(t * 0.3), 1.0)
          );

          float dist = distance(v_texcoord, center) * scale;
          if (dist < 1.0) {
            petals += 0.1 * (1.0 - dist);
          }
        }

        // Add pink petal accents where they're floating
        if (petals > 0.0) {
          color.rgb = mix(color.rgb, vec3(1.0, 0.8, 0.9), petals);
        }

        // Very subtle vignette
        float vignette = 1.0 - 0.1 * length(v_texcoord - 0.5);
        color.rgb *= vignette;

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Season - Summer post-processor
 */
class SummerPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Enhance warm sunlight
        color.r = min(1.0, color.r * 1.1);
        color.g = min(1.0, color.g * 1.05);

        // Increase saturation for vibrant colors
        float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
        vec3 saturatedColor = mix(vec3(luminance), color.rgb, 1.2);
        color.rgb = mix(color.rgb, saturatedColor, 0.3);

        // Add subtle golden tint for sun-drenched look
        vec3 sunlightTint = vec3(1.0, 0.95, 0.8);
        color.rgb = mix(color.rgb, color.rgb * sunlightTint, 0.2);

        // Add heat haze distortion effect at the bottom
        if (v_texcoord.y > 0.7) {
          float distortionStrength = (v_texcoord.y - 0.7) / 0.3 * 0.01;
          float distortion = sin((v_texcoord.x + u_time * 0.3) * 20.0) * distortionStrength;

          // Apply subtle wavy distortion
          vec2 distortedCoord = vec2(v_texcoord.x, v_texcoord.y + distortion);
          if (distortedCoord.y >= 0.0 && distortedCoord.y <= 1.0) {
            vec4 distortedColor = texture(u_image, distortedCoord);
            color = mix(color, distortedColor, 0.5);
          }
        }

        // Occasional lens flare effect to simulate bright sunlight
        float flareTime = mod(u_time * 0.2, 15.0);
        if (flareTime < 1.0) {
          float flareX = mod(u_time * 0.05, 1.0);
          float flareY = 0.3;
          float distToFlare = distance(v_texcoord, vec2(flareX, flareY));

          if (distToFlare < 0.1) {
            float flare = 0.3 * (1.0 - distToFlare/0.1) * (1.0 - flareTime);
            color.rgb += vec3(flare, flare * 0.8, flare * 0.5);
          }
        }

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Season - Fall/Autumn post-processor
 */
class FallPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      // Function to create a random falling leaf
      float leaf(vec2 uv, vec2 center, float size, float angle, float time) {
        // Rotate coordinates to create leaf shape
        float c = cos(angle);
        float s = sin(angle);
        vec2 rotatedUV = vec2(
          c * (uv.x - center.x) + s * (uv.y - center.y),
          -s * (uv.x - center.x) + c * (uv.y - center.y)
        );

        // Oval leaf shape
        float leafShape = 1.0 - smoothstep(0.0, size,
          length(vec2(rotatedUV.x * 1.5, rotatedUV.y * 0.8)));

        return leafShape * 0.1; // Return leaf intensity
      }

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Warm autumn colors
        // Increase reds and yellows, reduce blues and greens
        color.r = min(1.0, color.r * 1.2);
        color.g = min(1.0, color.g * 1.0);
        color.b = max(0.0, color.b * 0.9);

        // Overall warm golden tint
        vec3 autumnTint = vec3(0.9, 0.6, 0.3);
        color.rgb = mix(color.rgb, color.rgb * autumnTint, 0.2);

        // Convert green areas to fall colors
        if (color.g > color.r && color.g > color.b) {
          float greenAmount = color.g - max(color.r, color.b);
          vec3 fallColors = vec3(0.8, 0.4, 0.1); // Orange/red
          color.rgb = mix(color.rgb, fallColors, greenAmount * 0.5);
        }

        // Add falling leaves
        float leaves = 0.0;
        for (int i = 0; i < 8; i++) {
          float t = u_time * 0.2 + float(i) * 1.5;
          float downSpeed = 0.1 + float(i) * 0.02;
          float sideSpeed = 0.1;
          float size = 0.01 + float(i) * 0.002;

          // Leaf position with swaying motion
          vec2 leafPos = vec2(
            mod(0.3 + float(i) * 0.1 + sin(t * sideSpeed) * 0.2, 1.0),
            mod(t * downSpeed, 1.0)
          );

          // Rotating leaf angle
          float angle = t + float(i);

          // Calculate leaf contribution
          leaves += leaf(v_texcoord, leafPos, size, angle, t);
        }

        // Add orange/red/brown leaves
        if (leaves > 0.0) {
          // Vary leaf colors slightly based on random position
          float leafRandom = fract(sin(v_texcoord.x * 12.3456 + v_texcoord.y * 65.4321) * 43758.5453);
          vec3 leafColor;

          if (leafRandom < 0.33) {
            leafColor = vec3(0.8, 0.3, 0.1); // Red leaf
          } else if (leafRandom < 0.66) {
            leafColor = vec3(0.9, 0.6, 0.1); // Yellow/orange leaf
          } else {
            leafColor = vec3(0.5, 0.3, 0.1); // Brown leaf
          }

          color.rgb = mix(color.rgb, leafColor, leaves);
        }

        // Add subtle vignette
        float vignette = 1.0 - 0.15 * length(v_texcoord - 0.5);
        color.rgb *= vignette;

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Season - Winter post-processor
 */
class WinterPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0;

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time;
      in vec2 v_texcoord;
      out vec4 fragColor;

      // Function to create snowflake pattern
      float snowflake(vec2 uv, vec2 center, float size) {
        float dist = distance(uv, center) / size;
        return smoothstep(1.0, 0.95, dist) * 0.2;
      }

      void main() {
        vec4 color = texture(u_image, v_texcoord);

        // Cool blue-white tint
        color.r *= 0.9;
        color.g *= 0.95;
        color.b = min(1.0, color.b * 1.1);

        // Desaturate slightly for winter look
        float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
        color.rgb = mix(color.rgb, vec3(luminance), 0.15);

        // Add overall cool tint
        vec3 winterTint = vec3(0.8, 0.9, 1.0);
        color.rgb = mix(color.rgb, color.rgb * winterTint, 0.25);

        // Add frost to the edges (top and sides)
        float edgeX = min(v_texcoord.x, 1.0 - v_texcoord.x) * 10.0;
        float edgeY = min(v_texcoord.y, 1.0 - v_texcoord.y) * 10.0;
        float edge = min(edgeX, edgeY);

        if (edge < 1.0) {
          // Create frost pattern with noise
          float noise = fract(sin(v_texcoord.x * 100.0 + v_texcoord.y * 100.0) * 5000.0);
          float frostPattern = smoothstep(0.0, 1.0, noise + edge * 0.5);

          // Add frost highlights
          if (frostPattern < 0.2) {
            vec3 frost = vec3(0.9, 0.95, 1.0);
            color.rgb = mix(color.rgb, frost, (0.2 - frostPattern) * 1.5);
          }
        }

        // Add falling snowflakes
        float snow = 0.0;
        for (int i = 0; i < 10; i++) {
          float t = u_time * 0.2 + float(i) * 1.0;
          float downSpeed = 0.05 + float(i) * 0.01;
          float sideSpeed = 0.05;
          float size = 0.003 + float(i) * 0.001;

          // Snowflake position with gentle swaying
          vec2 snowPos = vec2(
            mod(0.2 + float(i) * 0.07 + sin(t * sideSpeed) * 0.1, 1.0),
            mod(t * downSpeed, 1.0)
          );

          // Add snowflake
          snow += snowflake(v_texcoord, snowPos, size);
        }

        // Combine snowflakes with scene
        color.rgb += vec3(snow);

        // Add subtle cold vignette
        float vignette = 1.0 - 0.2 * length(v_texcoord - 0.5);
        color.rgb = mix(color.rgb, color.rgb * vec3(0.8, 0.9, 1.0), 1.0 - vignette);

        fragColor = color;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Enhanced bloom post-processor (standalone)
 */
class BloomPostProcessor implements ex.PostProcessor {
  private _shader: ex.ScreenShader;
  private _time: number = 0; // Add time tracking for consistency

  initialize(gl: WebGL2RenderingContext): void {
    this._shader = new ex.ScreenShader(gl,
      `#version 300 es
      precision mediump float;
      uniform sampler2D u_image;
      uniform float u_time; // Add time uniform for animated bloom effects
      in vec2 v_texcoord;
      out vec4 fragColor;

      // Bloom effect parameters
      const float THRESHOLD = 0.7; // Brightness threshold
      const float INTENSITY = 0.5; // Bloom intensity
      const int SAMPLES = 10;     // Number of blur samples
      const float BLUR_SIZE = 0.004; // Blur sample distance

      // Helper function to check if a color is bright enough for bloom
      bool isBright(vec3 color) {
        float brightness = dot(color, vec3(0.299, 0.587, 0.114));
        return brightness > THRESHOLD;
      }

      void main() {
        vec4 baseColor = texture(u_image, v_texcoord);

        // Extract bright parts
        vec4 brightColor = vec4(0.0);
        if (isBright(baseColor.rgb)) {
          brightColor = baseColor;
        }

        // Apply blur to bright parts (horizontal and vertical passes)
        vec4 blurColor = vec4(0.0);
        for (int i = -SAMPLES; i <= SAMPLES; i++) {
          // Horizontal sample
          vec2 hOffset = vec2(float(i) * BLUR_SIZE, 0.0);
          vec4 hSample = texture(u_image, v_texcoord + hOffset);

          // Vertical sample
          vec2 vOffset = vec2(0.0, float(i) * BLUR_SIZE);
          vec4 vSample = texture(u_image, v_texcoord + vOffset);

          // Weight sample by distance from center and brightness
          float weight = float(SAMPLES - abs(i)) / float(SAMPLES);

          if (isBright(hSample.rgb)) {
            blurColor += hSample * weight * 0.5;
          }

          if (isBright(vSample.rgb)) {
            blurColor += vSample * weight * 0.5;
          }
        }

        // Normalize blur color
        blurColor /= float(SAMPLES) * 2.0;

        // Add bloom to original color
        // Add subtle pulse effect based on time
        float pulse = 1.0 + sin(u_time * 0.5) * 0.1; // Subtle 10% pulse
        vec4 finalColor = baseColor + blurColor * INTENSITY * pulse;

        fragColor = finalColor;
      }`
    );
  }

  getLayout(): ex.VertexLayout {
    return this._shader.getLayout();
  }

  getShader(): ex.Shader {
    return this._shader.getShader();
  }

  /**
   * Update shader uniforms
   */
  update(elapsed: number): void {
    this._time += elapsed / 1000;
    this._shader.getShader().trySetUniformFloat('u_time', this._time);
  }
}

/**
 * Manager for post-processing effects
 */
export class PostProcessorManager {
  private static instance: PostProcessorManager;
  private engine: ex.Engine | null = null;

  // Post processors - Basic effects
  private grayscaleProcessor: GrayscalePostProcessor;
  private retroScreenProcessor: RetroScreenPostProcessor;
  private pixelArtProcessor: PixelArtPostProcessor;
  private bloomProcessor: BloomPostProcessor;

  // Time of day processors
  private dawnProcessor: DawnPostProcessor;
  private dayProcessor: DayPostProcessor;
  private sunsetProcessor: SunsetPostProcessor;
  private nightProcessor: NightPostProcessor;

  // Season processors
  private springProcessor: SpringPostProcessor;
  private summerProcessor: SummerPostProcessor;
  private fallProcessor: FallPostProcessor;
  private winterProcessor: WinterPostProcessor;

  // Active effects tracking
  private activeProcessors: Map<string, ex.PostProcessor> = new Map();

  // Private constructor for singleton
  private constructor() {
    // Initialize basic effects
    this.grayscaleProcessor = new GrayscalePostProcessor();
    this.retroScreenProcessor = new RetroScreenPostProcessor();
    this.pixelArtProcessor = new PixelArtPostProcessor();
    this.bloomProcessor = new BloomPostProcessor();

    // Initialize time of day processors
    this.dawnProcessor = new DawnPostProcessor();
    this.dayProcessor = new DayPostProcessor();
    this.sunsetProcessor = new SunsetPostProcessor();
    this.nightProcessor = new NightPostProcessor();

    // Initialize season processors
    this.springProcessor = new SpringPostProcessor();
    this.summerProcessor = new SummerPostProcessor();
    this.fallProcessor = new FallPostProcessor();
    this.winterProcessor = new WinterPostProcessor();
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): PostProcessorManager {
    if (!PostProcessorManager.instance) {
      PostProcessorManager.instance = new PostProcessorManager();
    }
    return PostProcessorManager.instance;
  }

  /**
   * Initialize the post-processor manager with a reference to the engine
   */
  public initialize(engine: ex.Engine): void {
    this.engine = engine;

    // Check if WebGL is supported
    if (!engine.graphicsContext) {
      console.warn('Post-processing requires WebGL. Effects will be disabled.');
      return;
    }
  }

  /**
   * Update post-processors
   * Call every frame
   */
  public update(delta: number): void {
    // Update any processors that need time updates
    if (this.isEffectActive('retro')) {
      this.retroScreenProcessor.update(delta);
    }

    // Update bloom processor
    if (this.isEffectActive('bloom')) {
      this.bloomProcessor.update(delta);
    }

    // Update time-of-day processors
    if (this.isEffectActive('dawn')) {
      this.dawnProcessor.update(delta);
    }
    if (this.isEffectActive('day')) {
      this.dayProcessor.update(delta);
    }
    if (this.isEffectActive('sunset')) {
      this.sunsetProcessor.update(delta);
    }
    if (this.isEffectActive('night')) {
      this.nightProcessor.update(delta);
    }

    // Update season processors
    if (this.isEffectActive('spring')) {
      this.springProcessor.update(delta);
    }
    if (this.isEffectActive('summer')) {
      this.summerProcessor.update(delta);
    }
    if (this.isEffectActive('fall')) {
      this.fallProcessor.update(delta);
    }
    if (this.isEffectActive('winter')) {
      this.winterProcessor.update(delta);
    }
  }

  // Basic effect toggles

  /**
   * Toggle grayscale effect
   */
  public toggleGrayscale(): boolean {
    return this.toggleEffect('grayscale', this.grayscaleProcessor);
  }

  /**
   * Toggle retro screen effect
   */
  public toggleRetroScreen(): boolean {
    return this.toggleEffect('retro', this.retroScreenProcessor);
  }

  /**
   * Toggle pixel art effect
   */
  public togglePixelArt(): boolean {
    return this.toggleEffect('pixelart', this.pixelArtProcessor);
  }

  /**
   * Toggle bloom effect
   */
  public toggleBloom(): boolean {
    return this.toggleEffect('bloom', this.bloomProcessor);
  }

  // Time of day toggles

  /**
   * Toggle dawn effect
   */
  public toggleDawn(): boolean {
    this.clearTimeOfDay();
    return this.toggleEffect('dawn', this.dawnProcessor);
  }

  /**
   * Toggle day effect
   */
  public toggleDay(): boolean {
    this.clearTimeOfDay();
    return this.toggleEffect('day', this.dayProcessor);
  }

  /**
   * Toggle sunset effect
   */
  public toggleSunset(): boolean {
    this.clearTimeOfDay();
    return this.toggleEffect('sunset', this.sunsetProcessor);
  }

  /**
   * Toggle night effect
   */
  public toggleNight(): boolean {
    this.clearTimeOfDay();
    return this.toggleEffect('night', this.nightProcessor);
  }

  /**
   * Toggle night mode effect (for backward compatibility with existing UI)
   */
  public toggleNightMode(): boolean {
    return this.toggleNight();
  }

  /**
   * Clear all time of day effects
   */
  private clearTimeOfDay(): void {
    const timeEffects = ['dawn', 'day', 'sunset', 'night'];
    for (const effect of timeEffects) {
      if (this.isEffectActive(effect)) {
        this.toggleEffect(effect, this.getProcessorByName(effect));
      }
    }
  }

  // Season toggles

  /**
   * Toggle spring effect
   */
  public toggleSpring(): boolean {
    this.clearSeasons();
    return this.toggleEffect('spring', this.springProcessor);
  }

  /**
   * Toggle summer effect
   */
  public toggleSummer(): boolean {
    this.clearSeasons();
    return this.toggleEffect('summer', this.summerProcessor);
  }

  /**
   * Toggle fall/autumn effect
   */
  public toggleFall(): boolean {
    this.clearSeasons();
    return this.toggleEffect('fall', this.fallProcessor);
  }

  /**
   * Toggle winter effect
   */
  public toggleWinter(): boolean {
    this.clearSeasons();
    return this.toggleEffect('winter', this.winterProcessor);
  }

  /**
   * Clear all season effects
   */
  private clearSeasons(): void {
    const seasonEffects = ['spring', 'summer', 'fall', 'winter'];
    for (const effect of seasonEffects) {
      if (this.isEffectActive(effect)) {
        this.toggleEffect(effect, this.getProcessorByName(effect));
      }
    }
  }

  /**
   * Helper function to toggle an effect
   */
  private toggleEffect(name: string, processor: ex.PostProcessor): boolean {
    if (!this.engine?.graphicsContext) return false;

    if (this.isEffectActive(name)) {
      // Remove effect
      this.engine.graphicsContext.removePostProcessor(processor);
      this.activeProcessors.delete(name);
      return false;
    } else {
      // Add effect
      this.engine.graphicsContext.addPostProcessor(processor);
      this.activeProcessors.set(name, processor);
      return true;
    }
  }

  /**
   * Get processor by name
   */
  private getProcessorByName(name: string): ex.PostProcessor {
    switch (name) {
      case 'grayscale': return this.grayscaleProcessor;
      case 'retro': return this.retroScreenProcessor;
      case 'pixelart': return this.pixelArtProcessor;
      case 'bloom': return this.bloomProcessor;
      case 'dawn': return this.dawnProcessor;
      case 'day': return this.dayProcessor;
      case 'sunset': return this.sunsetProcessor;
      case 'night': return this.nightProcessor;
      case 'spring': return this.springProcessor;
      case 'summer': return this.summerProcessor;
      case 'fall': return this.fallProcessor;
      case 'winter': return this.winterProcessor;
      default: return this.grayscaleProcessor; // Fallback
    }
  }

  /**
   * Check if an effect is active
   */
  public isEffectActive(name: string): boolean {
    return this.activeProcessors.has(name);
  }

  /**
   * Remove all post-processors
   */
  public clearAllEffects(): void {
    if (!this.engine?.graphicsContext) return;

    for (const processor of this.activeProcessors.values()) {
      this.engine.graphicsContext.removePostProcessor(processor);
    }

    this.activeProcessors.clear();
  }
}
